import { parse } from 'date-fns';
import { TFunction } from 'i18next';
import { NarrativeFormData, SelectOption, SourceOption } from '../components/Forms/types';
import { API } from '../API';
import { languageOptions, languageOptionsUkrainian, languageNumberMapping } from './languages';
import { platformMapping } from './platforms';
import { contentTypesOptions } from './contentTypes';
import { IoCTypeOptionsList } from './actors';
import { getAffiliationTypeOptionsByValues } from './affiliationTypes';

// Create reverse platform mapping (ID -> name)
const platformIdToName: Record<number, string> = {};
for (const [name, id] of Object.entries(platformMapping)) {
  platformIdToName[id] = name;
}

const getSentimentOptions = (t: TFunction): SelectOption[] => [
  { label: t('Positive'), value: 'positive' },
  { label: t('Neutral'), value: 'neutral' },
  { label: t('Negative'), value: 'negative' },
];

async function fetchCountryLabels(ids: string[]): Promise<SelectOption[]> {
  if (ids.length === 0) return [];

  try {
    const data = await API.fetch('GET', '/API/v1/countries');
    const allCountries: any[] = data.objects || data || [];
    const idSet = new Set(ids);
    return allCountries
      .filter((country: any) => country && idSet.has(String(country.id)))
      .map((country: any) => ({ value: country.id, label: country.name }));
  } catch (error) {
    console.error('Failed to fetch countries (bulk):', error);
    // Return IDs as labels as fallback
    return ids.map(id => ({ value: id, label: id }));
  }
}

async function fetchSourceGroupLabels(ids: number[]): Promise<SelectOption[]> {
  if (ids.length === 0) return [];

  try {
    const promises = ids.map((groupId) =>
      API.fetch('GET', `/API/v1/groups/${groupId}`)
    );
    const results = await Promise.all(promises);
    return results
      .filter((v: any) => v && v.id)
      .map((v: any) => ({ value: v.id, label: v.name }));
  } catch (error) {
    console.error('Failed to fetch source groups:', error);
    return ids.map(id => ({ value: id, label: `Group ${id}` }));
  }
}

async function fetchSourceLabels(ids: number[]): Promise<SourceOption[]> {
  if (ids.length === 0) return [];

  try {
    const urlParams = new URLSearchParams();
    ids.forEach(id => urlParams.append('ids', String(id)));

    urlParams.set('size', String(ids.length));
    const data = await API.fetch('GET', `/API/v2/actors?${urlParams.toString()}`);

    return data.objects.map((actor: any) => ({
      value: actor.id,
      label: actor.name || actor.url || `Actor ${actor.id}`,
      icon: actor.platform?.toLowerCase(),
      followers_count: actor.followers_cnt,
    }));
  } catch (error) {
    console.error('Failed to fetch sources:', error);
    return ids.map(id => ({ value: id, label: `Actor ${id}` }));
  }
}

async function fetchFeedLabels(ids: number[]): Promise<SelectOption[]> {
  if (ids.length === 0) return [];

  try {
    const urlParams = new URLSearchParams();
    ids.forEach(id => urlParams.append('ids', String(id)));

    urlParams.set('size', String(ids.length));
    const data = await API.fetch('GET', `/API/v1/feeds?${urlParams.toString()}`);

    return (data.objects || [])
      .filter((feed: any) => feed && feed.id)
      .map((feed: any) => ({ value: feed.id, label: feed.name }));
  } catch (error) {
    console.error('Failed to fetch feeds:', error);
    return ids.map(id => ({ value: id, label: `Feed ${id}` }));
  }
}


function getLanguageOptionsByIds(ids: number[], lang: string): SelectOption[] {
  const langOptions = lang === 'uk' ? languageOptionsUkrainian : languageOptions;
  const result: SelectOption[] = [];
  for (const id of ids) {
    const code = languageNumberMapping[id];
    if (!code) continue;
    const option = langOptions.find(opt => opt.value === code);
    if (option) {
      result.push({ value: option.value, label: option.label });
    }
  }
  return result;
}

function getPlatformNamesByIds(ids: number[]): string[] {
  return ids
    .map(id => platformIdToName[id])
    .filter((name): name is string => name !== undefined);
}

function getContentTypesByValues(values: string[]): string[] {
  const validTypes = contentTypesOptions.map(opt => opt.value);
  return values.filter(v => validTypes.includes(v));
}

function getAffiliationTypesByValues(t: TFunction, values: string[]): SelectOption[] {
  return getAffiliationTypeOptionsByValues(t, values);
}

function getDiscreditationsByValues(t: TFunction, values: string[]): SelectOption[] {
  const iocOptions = IoCTypeOptionsList(t) as SelectOption[];
  const result: SelectOption[] = [];
  for (const v of values) {
    const opt = iocOptions.find((o) => o.value === v);
    if (opt) {
      result.push(opt);
    }
  }
  return result;
}

function getSentimentsByValues(t: TFunction, values: string[]): SelectOption[] {
  const sentimentOpts = getSentimentOptions(t);
  return values
    .map(v => sentimentOpts.find((opt: SelectOption) => opt.value === v))
    .filter((opt): opt is SelectOption => opt !== undefined);
}

/**
 * Parses URL parameters and converts to NarrativeFormData for prefilling the form.
 * Handles both static lookups (languages, platforms) and API calls (countries, sources, etc.)
 * @param searchParams - URL search params with form_* prefix
 * @param t - Translation function from i18next
 * @param lang - Current language code (e.g., 'en', 'uk')
 */
export async function parseFormParamsToInitialData(
  searchParams: URLSearchParams,
  t: TFunction,
  lang: string = 'en'
): Promise<Partial<NarrativeFormData>> {
  const initialData: Partial<NarrativeFormData> = {};

  const keywords = searchParams.get('form_keywords');
  if (keywords) {
    initialData.keywords = keywords.trim();
  }

  const startDateStr = searchParams.get('form_start_date');
  const endDateStr = searchParams.get('form_end_date');
  if (startDateStr) {
    initialData.startDate = parse(startDateStr, 'yyyy-MM-dd', new Date());
  }
  if (endDateStr) {
    initialData.endDate = parse(endDateStr, 'yyyy-MM-dd', new Date());
  }

  if (searchParams.get('form_multilingual') === 'true') {
    initialData.isMultilang = true;
  }

  const platformIds = searchParams.getAll('form_platforms').map(Number).filter(n => !isNaN(n));
  if (platformIds.length > 0) {
    initialData.sourceTypes = getPlatformNamesByIds(platformIds);
  }

  const languageIds = searchParams.getAll('form_languages').map(Number).filter(n => !isNaN(n));
  if (languageIds.length > 0) {
    initialData.languages = getLanguageOptionsByIds(languageIds, lang);
    initialData.languagesExclude = searchParams.get('form_languages_exclude') === 'true';
  }

  const contentTypes = searchParams.getAll('form_content_types');
  if (contentTypes.length > 0) {
    initialData.contentTypes = getContentTypesByValues(contentTypes);
  }

  const sentiments = searchParams.getAll('form_sentiment');
  if (sentiments.length > 0) {
    initialData.sentiment = getSentimentsByValues(t, sentiments);
  }

  const discreditations = searchParams.getAll('form_discreditations');
  if (discreditations.length > 0) {
    initialData.discreditations = getDiscreditationsByValues(t, discreditations);
    initialData.discreditationsExclude = searchParams.get('form_discreditations_exclude') === 'true';
  }

  const showBots = searchParams.get('form_show_bots');
  const botsExclude = searchParams.get('form_bots_exclude');
  if (showBots === 'true') {
    initialData.showBotsOnly = botsExclude === 'true' ? false : true;
  } else if (botsExclude === 'true') {
    initialData.showBotsOnly = false;
  }

  const affiliations = searchParams.getAll('form_affiliations');
  if (affiliations.length > 0) {
    if (affiliations[0] === 'all') {
      initialData.affiliationAll = true;
      initialData.affiliationCountries = [];
    } else {
      const affiliationCountries = await fetchCountryLabels(affiliations);
      initialData.affiliationCountries = affiliationCountries;
    }
    initialData.affiliationCountriesExclude = searchParams.get('form_affiliations_exclude') === 'true';
  }

  const geoGroupIds = searchParams.getAll('form_geo_group');
  if (geoGroupIds.length > 0) {
    initialData.geoGroup = geoGroupIds.map(id => parseInt(id));
    initialData.geoGroupExclude = searchParams.get('form_geo_group_exclude') === 'true';
  }

  const tagPaths = searchParams.getAll('form_tags');
  if (tagPaths.length > 0) {
    initialData.tags = tagPaths;
    initialData.tagsExclude = searchParams.get('form_tags_exclude') === 'true';
  }

  const affiliationTypeValues = searchParams.getAll('form_affiliation_types');
  if (affiliationTypeValues.length > 0) {
    initialData.affiliationTypes = getAffiliationTypesByValues(t, affiliationTypeValues);
    initialData.affiliationTypesExclude = searchParams.get('form_affiliation_types_exclude') === 'true';
  }

  // API calls for data that needs labels
  // Use Promise.allSettled so a single failed fetch doesn't prevent other fields from being populated
  type ApiTaskKey = 'countries' | 'sourceGroups' | 'sources' | 'feeds';
  interface ApiTask {
    key: ApiTaskKey;
    excludeParam: string;
    promise: Promise<SelectOption[] | SourceOption[]>;
  }

  const apiTasks: ApiTask[] = [];

  const countryIds = searchParams.getAll('form_countries');
  if (countryIds.length > 0) {
    apiTasks.push({
      key: 'countries',
      excludeParam: 'form_countries_exclude',
      promise: fetchCountryLabels(countryIds),
    });
  }

  const sourceGroupIds = searchParams.getAll('form_source_groups').map(Number).filter(n => !isNaN(n));
  if (sourceGroupIds.length > 0) {
    apiTasks.push({
      key: 'sourceGroups',
      excludeParam: 'form_source_groups_exclude',
      promise: fetchSourceGroupLabels(sourceGroupIds),
    });
  }

  const sourceIds = searchParams.getAll('form_sources').map(Number).filter(n => !isNaN(n));
  if (sourceIds.length > 0) {
    apiTasks.push({
      key: 'sources',
      excludeParam: 'form_sources_exclude',
      promise: fetchSourceLabels(sourceIds),
    });
  }

  const feedIds = searchParams.getAll('form_feeds').map(Number).filter(n => !isNaN(n));
  if (feedIds.length > 0) {
    apiTasks.push({
      key: 'feeds',
      excludeParam: 'form_feeds_exclude',
      promise: fetchFeedLabels(feedIds),
    });
  }

  const results = await Promise.allSettled(apiTasks.map(task => task.promise));

  results.forEach((result, index) => {
    const task = apiTasks[index];
    const excludeValue = searchParams.get(task.excludeParam) === 'true';

    if (result.status === 'fulfilled') {
      switch (task.key) {
        case 'countries':
          initialData.countries = result.value as SelectOption[];
          initialData.countriesExclude = excludeValue;
          break;
        case 'sourceGroups':
          initialData.sourceGroups = result.value as SelectOption[];
          initialData.sourceGroupsExclude = excludeValue;
          break;
        case 'sources':
          initialData.sources = result.value as SourceOption[];
          initialData.sourcesExclude = excludeValue;
          break;
        case 'feeds':
          initialData.feeds = result.value as SelectOption[];
          initialData.feedsExclude = excludeValue;
          break;
      }
    } else {
      // Handle rejection - set safe defaults and log error
      console.error(`Failed to fetch ${task.key}:`, result.reason);
      switch (task.key) {
        case 'countries':
          initialData.countries = [];
          initialData.countriesExclude = false;
          break;
        case 'sourceGroups':
          initialData.sourceGroups = [];
          initialData.sourceGroupsExclude = false;
          break;
        case 'sources':
          initialData.sources = [];
          initialData.sourcesExclude = false;
          break;
        case 'feeds':
          initialData.feeds = [];
          initialData.feedsExclude = false;
          break;
      }
    }
  });

  return initialData;
}

/**
 * Checks if the URL has any form prefill parameters
 */
export function hasFormParams(searchParams: URLSearchParams): boolean {
  return Array.from(searchParams.keys()).some(key => key.startsWith('form_'));
}
